import { useMutation, useQuery } from '@tanstack/react-query'
import { RefreshCcw, Save, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  createAdminUser,
  fetchAdminUsers,
  type AdminUser,
  type AdminUserCreate,
  type AdminUserUpdate,
  type UserRole,
  updateAdminUser
} from '../lib/api'
import { useAuth } from '../lib/auth'

const roleOptions: UserRole[] = ['admin', 'analyst', 'viewer']

type NewUserForm = {
  email: string
  password: string
  role: UserRole
}

const emptyNewUserForm: NewUserForm = {
  email: '',
  password: '',
  role: 'analyst'
}

export function UsersPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin'
  const [newUser, setNewUser] = useState<NewUserForm>(emptyNewUserForm)
  const [localError, setLocalError] = useState<string | null>(null)

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: fetchAdminUsers,
    enabled: isAdmin
  })

  const createMutation = useMutation({
    mutationFn: createAdminUser,
    onSuccess: () => {
      setNewUser(emptyNewUserForm)
      setLocalError(null)
      void usersQuery.refetch()
    },
    onError: (error: Error) => {
      setLocalError(error.message)
    }
  })

  const updateMutation = useMutation({
    mutationFn: updateAdminUser,
    onSuccess: () => {
      setLocalError(null)
      void usersQuery.refetch()
    },
    onError: (error: Error) => {
      setLocalError(error.message)
    }
  })

  function submitNewUser() {
    const payload = createPayload(newUser)
    if (!payload) {
      setLocalError('Email and an 8+ character password are required')
      return
    }
    setLocalError(null)
    createMutation.mutate(payload)
  }

  function saveUser(userId: string, update: AdminUserUpdate) {
    updateMutation.mutate({ userId, update })
  }

  const users = usersQuery.data ?? []
  const errorMessage = localError ?? usersQuery.error?.message ?? null
  const pendingUserId = updateMutation.isPending ? updateMutation.variables?.userId ?? null : null

  return (
    <section className="usersPage">
      <div className="toolbar">
        <div>
          <p className="eyebrow">Access</p>
          <h1>Users</h1>
        </div>
        <button
          aria-label="Refresh users"
          className="secondaryButton"
          type="button"
          onClick={() => {
            void usersQuery.refetch()
          }}
          disabled={!isAdmin || usersQuery.isFetching}
        >
          <RefreshCcw size={18} aria-hidden="true" />
          <span>{usersQuery.isFetching ? 'Refreshing' : 'Refresh'}</span>
        </button>
      </div>

      {!isAdmin ? (
        <section className="usersPanel">
          <div className="settingsEmpty">Admin role required</div>
        </section>
      ) : (
        <>
          <section className="usersPanel" aria-labelledby="create-user-title">
            <div className="settingsPanelHeader">
              <div>
                <p className="eyebrow">Identity</p>
                <h2 id="create-user-title">Create User</h2>
              </div>
              <span className="jobsMeta">{users.length} users</span>
            </div>
            <div className="usersForm">
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(event) => {
                    setNewUser((current) => ({ ...current, email: event.target.value }))
                  }}
                />
              </label>
              <label>
                <span>Role</span>
                <select
                  value={newUser.role}
                  onChange={(event) => {
                    setNewUser((current) => ({
                      ...current,
                      role: roleFromValue(event.target.value)
                    }))
                  }}
                >
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Password</span>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(event) => {
                    setNewUser((current) => ({ ...current, password: event.target.value }))
                  }}
                />
              </label>
              <button
                className="primaryButton"
                type="button"
                onClick={submitNewUser}
                disabled={createMutation.isPending}
              >
                <UserPlus size={18} aria-hidden="true" />
                <span>{createMutation.isPending ? 'Creating' : 'Create'}</span>
              </button>
            </div>
            {errorMessage ? <span className="settingsError">{errorMessage}</span> : null}
          </section>

          <section className="usersPanel" aria-labelledby="users-table-title">
            <div className="settingsPanelHeader">
              <div>
                <p className="eyebrow">Accounts</p>
                <h2 id="users-table-title">Active Users</h2>
              </div>
            </div>
            <div className="tableFrame usersTableFrame">
              <table className="usersTable">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last login</th>
                    <th>Password reset</th>
                    <th>Save</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        isSaving={pendingUserId === user.id}
                        onSave={saveUser}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="jobsEmpty">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </section>
  )
}

type UserRowProps = {
  user: AdminUser
  isSaving: boolean
  onSave: (userId: string, update: AdminUserUpdate) => void
}

function UserRow({ user, isSaving, onSave }: UserRowProps) {
  const [role, setRole] = useState<UserRole>(user.role)
  const [password, setPassword] = useState('')
  const [rowError, setRowError] = useState<string | null>(null)

  useEffect(() => {
    setRole(user.role)
    setPassword('')
    setRowError(null)
  }, [user.id, user.role])

  function saveChanges() {
    const trimmedPassword = password.trim()
    const roleChanged = role !== user.role
    if (!roleChanged && !trimmedPassword) {
      setRowError('No changes')
      return
    }
    if (trimmedPassword && trimmedPassword.length < 8) {
      setRowError('Password must be 8+ characters')
      return
    }
    const update: AdminUserUpdate = {}
    if (roleChanged) {
      update.role = role
    }
    if (trimmedPassword) {
      update.password = trimmedPassword
    }
    setRowError(null)
    onSave(user.id, update)
  }

  return (
    <tr>
      <td>
        <strong>{user.email}</strong>
        <small>{formatDate(user.created_at)}</small>
      </td>
      <td>
        <select
          className="userRoleSelect"
          value={role}
          onChange={(event) => {
            setRole(roleFromValue(event.target.value))
          }}
        >
          {roleOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </td>
      <td>{formatDate(user.last_login_at)}</td>
      <td>
        <input
          className="userPasswordInput"
          type="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />
        {rowError ? <span className="settingsError">{rowError}</span> : null}
      </td>
      <td>
        <button
          aria-label={`Save ${user.email}`}
          className="iconButton"
          type="button"
          onClick={saveChanges}
          disabled={isSaving}
          title="Save user"
        >
          <Save size={16} aria-hidden="true" />
        </button>
      </td>
    </tr>
  )
}

function createPayload(form: NewUserForm): AdminUserCreate | null {
  const email = form.email.trim()
  const password = form.password.trim()
  if (!email || !email.includes('@') || password.length < 8) {
    return null
  }
  return { email, password, role: form.role }
}

function roleFromValue(value: string): UserRole {
  if (value === 'admin' || value === 'analyst' || value === 'viewer') {
    return value
  }
  return 'viewer'
}

function formatDate(value: string | null): string {
  if (!value) {
    return '-'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
